import styled from "styled-components";
import { auth, Utils } from "@tiffin/app-common";
import { useAppSelector } from "../hooks";
import { Badge, Menu } from "antd";
import { useLocation, useNavigate, useSearchParams} from 'react-router-dom'
import { IonIcon } from "@ionic/react";
import { gridOutline, logInOutline, receiptOutline, personOutline, cartOutline, chatboxOutline } from 'ionicons/icons';
import type { MenuProps } from 'antd';


type MenuItem = Required<MenuProps>['items'][number];

export const NavBar = styled(BaseNavBar)`
  width: auto;
  height: 100%;
  transition: width 0.2s;
  overflow: auto;
  z-index: 999;

  .ant-menu {
    position: relative;
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;

    .ant-menu-submenu-title {
      height: 64px;
      display: flex;
      align-items: center;

      ion-icon {
        font-size: 2em !important;
      }
    }

    .ant-menu-item  {
      display: grid;
      grid-template-columns: auto 1fr;
      height: 64px;
      align-items: center;

      ion-icon {
        font-size: 2em !important;
      }

      .ant-scroll-number-only {
        margin-top: 3px;
      }
    }

    .ant-menu-item-selected,
    .ant-menu-submenu-selected {
      background: rgba(var(--app-base-color-rgb), 0.1);
      color: var(--app-base-color);
      font-weight: 600;

      .ant-menu-title-content,
      .ant-menu-submenu-arrow {
        color: var(--app-base-color);
      }

      .ant-menu-item-icon {
        color: var(--app-base-color);
      }

      &::after {
        border-right: 4px solid var(--app-base-color);
      }
    }
  }
`

interface INavBar {
  className?: string
}

function BaseNavBar({className}: INavBar) {
  const authenticateStatus = useAppSelector((state) => state.authenticate.status)
  const partnerId = useAppSelector((state) => state.store.details?.id)
  const partnerUrlName = useAppSelector((state) => state.store.details?.urlName ? Utils.convertToLowerName(state.store.details?.urlName) : undefined)
  const cartItems = useAppSelector((state) => partnerId ? state.cartMulti.carts[partnerId]?.items : {});
  const conversationsMetrics = useAppSelector((state) => state.conversationsMetrics)

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  function numberofItems() {
    let totalCount = 0
    for (let date in cartItems) {
      let items = cartItems[date]
      for (let id in items) {
        totalCount += items[id].count
      }
    }
    return totalCount
  }

  function getSelectedKey() {
    if(location.pathname.startsWith(`/${partnerUrlName}/signin`)) {
      return 'signin'
    } else if(location.pathname.startsWith(`/${partnerUrlName}/account`)) {
      return 'account'
    } else if(location.pathname.startsWith(`/${partnerUrlName}/cart`) || location.pathname.startsWith(`/${partnerUrlName}/checkout`)) {
      return 'cart'
    } else if(location.pathname.startsWith(`/${partnerUrlName}/orders`)) {
      return 'orders'
    } else if(location.pathname.startsWith(`/${partnerUrlName}/messages`)) {
      return 'messages'
    } else if(location.pathname.startsWith(`/${partnerUrlName}`)) {
      return 'menu'
    }
    return ''
  }

  function getItem(
    label: React.ReactNode,
    key?: React.Key | null,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group',
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem;
  }

  const renderMenuItemIcon = (icon: string, badgeCount?: number) => <Badge count={badgeCount}><IonIcon icon={icon}/></Badge>
  
  const items: MenuItem[] = [
    getItem('Menu', 'menu', renderMenuItemIcon(gridOutline)),
    getItem('Cart', 'cart', renderMenuItemIcon(cartOutline, numberofItems())),
    getItem('Orders', 'orders', renderMenuItemIcon(receiptOutline)),
    getItem('Messages', 'messages', renderMenuItemIcon(chatboxOutline, conversationsMetrics.metrics?.customerUnread))
  ]

  if(authenticateStatus === "authenticated") {
    items.push(
      getItem('Account', 'account', renderMenuItemIcon(personOutline), [
        getItem('Edit details', 'edit-details'),
        getItem('Log out', 'signout'),
      ])
    )
  } else if(authenticateStatus === "unauthenticated") {
    items.push(getItem('Log in', 'signin', renderMenuItemIcon(logInOutline)))
  }

  const onClick: MenuProps['onClick'] = (e) => {
    if(e.key === "menu") {
      navigate({pathname: `/${partnerUrlName}`, search: searchParams.toString()})
    } else if(e.key === "cart") {
      navigate({pathname: `/${partnerUrlName}/cart`, search: searchParams.toString()})
    } else if(e.key === "orders") {
      navigate({pathname: `/${partnerUrlName}/orders`, search: searchParams.toString()})
    } else if(e.key === "messages") {
      navigate({pathname: `/${partnerUrlName}/messages`, search: searchParams.toString()})
    } else if(e.key === "account") {
      navigate({pathname: `/${partnerUrlName}/account`, search: searchParams.toString()})
    } else if(e.key === "signout") {
      if (auth.currentUser) {
        auth.signOut()
      }
      window.location.href = `/${partnerUrlName}${window.location.search}`
    } else if(e.key === "signin") {
      navigate({pathname: `/${partnerUrlName}/signin`, search: searchParams.toString()})
    } else if(e.key === "edit-details") {
      navigate({pathname: `/${partnerUrlName}/account`, search: searchParams.toString()})
    }
  };

  return (
    <div className={className}>
      <Menu
        defaultActiveFirst={true}
        onClick={onClick}
        items={items}
        selectedKeys={[getSelectedKey()]}
        mode="vertical"
      />
    </div>
  )
}